import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper,
  Stack,
  Button,
  Avatar,
  useTheme,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../utils/seo/SEO';
import WorkIcon from '@mui/icons-material/Work';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import Grid from '../utils/GridWrapper';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const About: React.FC = () => {
  const theme = useTheme();
  
  // Core values data
  const values = [
    {
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from the advice we give to the results we help our clients achieve.'
    },
    {
      title: 'Integrity',
      description: 'We operate with complete transparency and honesty, always putting our clients\' best interests first.'
    },
    {
      title: 'Innovation',
      description: 'We embrace innovative thinking and constantly seek new approaches to solve complex business challenges.'
    },
    {
      title: 'Results-Driven',
      description: 'We focus relentlessly on delivering measurable, impactful results that create real business value.'
    }
  ];
  
  // Expertise areas data - Updated based on resume
  const expertise = [
    'Product Strategy & Vision',
    'Cybersecurity Solutions',
    'SaaS Platforms',
    'Cloud Transformation',
    'GenAI Integration',
    'Go-to-Market Strategy',
    'Executive Partnerships',
    'Cross-Functional Leadership'
  ];
  
  // Career milestones based on updated resume
  const milestones = [
    {
      year: 'Aug 2024 – Apr 2025',
      title: 'Chief Strategy Officer (CSO)',
      organization: 'NetWitness',
      description: 'Developed enterprise-wide product vision, oversaw national sales operations, cultivated executive-level partnerships with CISOs and IT leaders, established high-impact corporate alliances, and led cross-functional product initiatives.'
    },
    {
      year: 'Sept 2022 – Aug 2024',
      title: 'Chief Product Officer (CPO)',
      organization: 'NetWitness',
      description: 'Directed cross-functional teams across engineering and product to develop a cloud-native SaaS threat detection platform, integrating GenAI to increase detection accuracy, automate triage, and strengthen decision-making across diverse cybersecurity environments.',
      media: {
        type: 'video',
        url: 'https://www.youtube.com/embed/dH7g1zG4uUQ',
        title: 'Interview as CPO at NetWitness'
      }
    },
    {
      year: 'May 2020 – Aug 2022',
      title: 'Acting CTO, Vice President of Development and Product',
      organization: 'Civix',
      description: 'Spearheaded cross-functional technology teams across business units, provided leadership in architecture and infrastructure, and delivered secure, scalable solutions supporting federal and state agencies.'
    },
    {
      year: 'Jan 2008 – May 2020',
      title: 'Vice President of Development and Product',
      organization: 'AccessData',
      description: 'Drove revenue acceleration and EBITDA growth by 333%, led cybersecurity innovation, transformed global support operations, and implemented cloud-first deployment strategies that directly contributed to successful company acquisition.'
    }
  ];

  // Baseball career achievements
  const baseballAchievements = [
    'Played baseball at the University of North Florida',
    'Received 2 First Team All-American Awards',
    'Recognized for the most wins in 2001',
    <React.Fragment>
      Inducted to the <a href="https://unfospreys.com/news/2022/12/9/general-north-florida-athletics-announces-2023-hall-of-fame-class.aspx" target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'underline' }}>University of North Florida Hall of Fame</a>
    </React.Fragment>,
    'Played professionally for the St. Louis Cardinals organization in A ball with the New Jersey Cardinals',
    'Promoted to High-A Potomac Cannons'
  ];

  return (
    <>
      <SEO 
        title="About Ewasko Consulting | 20+ Years of Product Leadership Experience"
        description="Learn about Tod Ewasko's 20+ years of expertise in product management, strategy consulting, and leadership across cybersecurity, SaaS, and public sector platforms."
        keywords="product management consultant, Tod Ewasko, cybersecurity expert, product strategy, SaaS leadership, cloud transformation"
        canonicalUrl="https://todfather.com/about"
        ogType="website"
        ogTitle="About Ewasko Consulting | Product Leadership Excellence"
        ogDescription="With 20+ years of experience in product leadership, Tod Ewasko helps businesses achieve strategic objectives through expert product management and operations consulting."
        ogImage="https://todfather.com/about-image.jpg"
        twitterCard="summary_large_image"
        twitterTitle="About Ewasko Consulting | Product Leadership Excellence"
        twitterDescription="With 20+ years of experience in product leadership, Tod Ewasko helps businesses achieve strategic objectives through expert product management and operations consulting."
        twitterImage="https://todfather.com/about-image.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Tod Ewasko",
          "url": "https://todfather.com/about",
          "image": "https://todfather.com/about-image.jpg",
          "sameAs": ["https://www.linkedin.com/in/todewasko/"],
          "jobTitle": "Product Strategy Consultant",
          "worksFor": {
            "@type": "Organization",
            "name": "Ewasko Consulting",
            "url": "https://todfather.com"
          },
          "description": "Technology Executive with 20+ years of experience in product leadership, strategy development, and engineering management.",
          "knowsAbout": ["Product Management", "Cybersecurity Solutions", "SaaS Platforms", "Cloud Transformation", "GenAI Integration", "Product Strategy"],
          "alumniOf": [
            {
              "@type": "Organization",
              "name": "NetWitness"
            },
            {
              "@type": "Organization",
              "name": "Civix"
            },
            {
              "@type": "Organization",
              "name": "AccessData"
            },
            {
              "@type": "CollegeOrUniversity",
              "name": "University of North Florida"
            }
          ],
          "hasOccupation": [
            {
              "@type": "Occupation",
              "name": "Chief Strategy Officer",
              "occupationLocation": { "@type": "Place", "name": "NetWitness" },
              "startDate": "2024-08",
              "endDate": "2025-04"
            },
            {
              "@type": "Occupation",
              "name": "Chief Product Officer",
              "occupationLocation": { "@type": "Place", "name": "NetWitness" },
              "startDate": "2022-09",
              "endDate": "2024-08"
            }
          ]
        }}
      />
      <Box>
        {/* Hero Section */}
        <Box 
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: { xs: 8, md: 12 },
            position: 'relative'
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeIn}
                >
                  <Typography 
                    variant="overline" 
                    component="div" 
                    sx={{ opacity: 0.8, fontWeight: 'bold', letterSpacing: 2 }}
                  >
                    About Us
                  </Typography>
                  <Typography 
                    variant="h2" 
                    component="h1"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    20 Years of Product Leadership Excellence
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 4, 
                      maxWidth: '800px',
                      opacity: 0.9
                    }}
                  >
                    Bringing decades of experience and proven expertise to help businesses achieve product excellence and strategic growth.
                  </Typography>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <Box 
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {/* Replace Avatar with Box component for full image */}
                    <Box 
                      component="img"
                      src="/about-image.jpg" 
                      alt="Founder"
                      sx={{
                        width: '100%', // Make image responsive
                        maxWidth: '400px', // Limit max width
                        height: 'auto', // Maintain aspect ratio
                        borderRadius: 2, // Optional: add slight rounding
                        boxShadow: theme.shadows[5] // Optional: add shadow like other images
                      }}
                    />
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Our Story Section - Updated */}
        <Box sx={{ py: { xs: 8, md: 10 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                >
                  <Typography variant="overline" component="div" color="primary" fontWeight="bold">
                    Our Story
                  </Typography>
                  <Typography variant="h3" component="h2" gutterBottom>
                    Driving Product Excellence Through Experience
                  </Typography>
                  {/* Updated paragraphs based on new resume */}
                  <Typography variant="body1" paragraph>
                    As a dynamic and results-driven Technology Executive with over 20 years of experience, I specialize in driving growth across cybersecurity, SaaS, and public sector platforms. My focus is on leading cross-functional teams, defining product vision and strategy, and delivering innovative solutions that align technology with business outcomes.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    My expertise spans cloud transformation, GenAI integration, and enterprise platforms. I excel at translating complex user needs into scalable solutions and building strong executive partnerships. With experience across product development, strategy, and go-to-market functions, I deliver high-value platforms to regulated sectors such as federal agencies and critical infrastructure.
                  </Typography>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                >
                  <Box
                    component="img"
                    src="/about-story.png"
                    alt="Our Journey"
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      boxShadow: theme.shadows[5]
                    }}
                  />
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>
        
        {/* Career Timeline - Updated with new milestones data and moved up */}
        <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'background.paper' }}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="overline" component="div" color="primary" fontWeight="bold">
                Professional Journey
              </Typography>
              <Typography variant="h3" component="h2" gutterBottom>
                Career Milestones
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
                A journey of growth, leadership, and impact across two decades in the technology industry.
              </Typography>
            </Box>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <Timeline position="alternate">
                {milestones.map((milestone, index) => (
                  <TimelineItem key={index}>
                    <TimelineSeparator>
                      <TimelineDot color="primary">
                        <WorkIcon />
                      </TimelineDot>
                      {index < milestones.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <motion.div variants={fadeIn}>
                        <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                          <Typography variant="caption" color="textSecondary" component="div">
                            {milestone.year}
                          </Typography>
                          <Typography variant="h6" component="h3" fontWeight="bold">
                            {milestone.title}
                          </Typography>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            {milestone.organization}
                          </Typography>
                          <Typography variant="body2">
                            {milestone.description}
                          </Typography>
                          {milestone.media && milestone.media.type === 'video' && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                {milestone.media.title}
                              </Typography>
                              <Box
                                component="iframe"
                                src={milestone.media.url}
                                title={milestone.media.title}
                                width="100%"
                                height="315"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </Box>
                          )}
                        </Paper>
                      </motion.div>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </motion.div>
          </Container>
        </Box>
        
        {/* Baseball Career Section - Moved below Professional Journey */}
        <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'background.default' }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                >
                  <Typography variant="overline" component="div" color="primary" fontWeight="bold">
                    Athletic Background
                  </Typography>
                  <Typography variant="h3" component="h2" gutterBottom>
                    Professional Baseball Career
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Before his successful career in technology, Tod was an accomplished baseball player with a remarkable collegiate and professional career.
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    {baseballAchievements.map((achievement, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          mb: 1.5
                        }}
                      >
                        <Box 
                          sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: 'primary.main', 
                            mr: 2 
                          }} 
                        />
                        <Typography variant="body1">{achievement}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="body1">
                    This athletic background has informed Tod's leadership approach, bringing the discipline, teamwork, and competitive drive from professional sports into the business world.
                  </Typography>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      component="img"
                      src="/Tod_BB.jpg"
                      alt="Tod Baseball Career"
                      sx={{
                        width: '100%',
                        maxWidth: '450px',
                        height: 'auto',
                        borderRadius: 2,
                        boxShadow: theme.shadows[5],
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>
        
        {/* Core Values Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="overline" component="div" color="primary" fontWeight="bold">
                Our Values
              </Typography>
              <Typography variant="h3" component="h2" gutterBottom>
                Guided by Strong Principles
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
                Our core values shape everything we do, from how we approach client relationships to the quality of work we deliver.
              </Typography>
            </Box>
            
            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Grid container spacing={4}>
                {values.map((value, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <motion.div variants={fadeIn}>
                      <Paper 
                        sx={{ 
                          p: 4, 
                          height: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            transform: 'translateY(-5px)',
                            boxShadow: theme.shadows[8]
                          }
                        }}
                        elevation={2}
                      >
                        <Typography variant="h5" component="h3" gutterBottom color="primary" fontWeight="bold">
                          {value.title}
                        </Typography>
                        <Typography variant="body1">
                          {value.description}
                        </Typography>
                      </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Container>
        </Box>
        
        {/* CTA Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, textAlign: 'center', bgcolor: 'background.default' }}>
          <Container maxWidth="md">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <Typography variant="h3" component="h2" gutterBottom>
                Let's Work Together
              </Typography>
              <Typography variant="body1" paragraph color="textSecondary" sx={{ mb: 4, maxWidth: '700px', mx: 'auto' }}>
                Ready to leverage our experience and expertise to transform your product strategy and drive business growth? Contact us today to start the conversation.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  component={RouterLink}
                  to="/contact"
                >
                  Contact Us
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary"
                  size="large"
                  component={RouterLink}
                  to="/services"
                >
                  Explore Our Services
                </Button>
              </Stack>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default About;